import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ArticleCard from "../components/ArticleCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, Loader2 } from "lucide-react";
import SEO from "../components/SEO";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/articles?search=${encodeURIComponent(searchQuery)}&limit=50`);
      setResults(response.data.articles);
    } catch (error) {
      console.error("Search failed:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  return (
    <div className="pt-16 min-h-screen" data-testid="search-page">
      <SEO 
        title={query ? `Search: ${query}` : "Search"}
        description="Search articles on Mancunia Rebél covering music, fashion, arts, film, photography and independent creatives."
        url="/search"
      />
      {/* Search Header */}
      <section className="py-12 md:py-16 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Search</span>
          <h1 className="font-heading font-black text-5xl md:text-7xl uppercase tracking-tighter mt-2">
            Find Content
          </h1>
          
          <form onSubmit={handleSubmit} className="mt-8 flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles, topics, artists..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-8 bg-transparent border-b border-border focus:border-primary rounded-none px-0 h-14 font-mono text-lg"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 font-mono uppercase tracking-widest text-xs"
              data-testid="search-submit"
            >
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : searched ? (
            <>
              <p className="font-mono text-sm text-muted-foreground mb-8 uppercase tracking-wider">
                {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
              </p>
              
              {results.length === 0 ? (
                <div className="text-center py-24">
                  <Search className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mb-2">No Results Found</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    We couldn't find any articles matching your search. Try different keywords or browse our categories.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                  {results.map((article) => (
                    <ArticleCard key={article.article_id} article={article} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-24">
              <Search className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mb-2">Search Our Archive</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                Enter keywords above to search through our articles, interviews, and cultural coverage.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SearchPage;
